import { type ReactNode, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HelpCircle } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { ProgressCard } from "../common/ProgressCard";
import { cn } from "@/lib/utils";

interface LeadershipCardProps {
  onCollapse: () => void;
}

function FieldBlock({
  label,
  description,
  children,
}: {
  label: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label className="text-sm font-medium leading-5 text-slate-900">{label}</Label>
        {description ? <p className="text-sm leading-5 text-slate-500">{description}</p> : null}
      </div>
      {children}
    </div>
  );
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[72px] cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary/20 bg-primary/10" : "border-slate-200 bg-white hover:border-slate-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-500",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}


export function LeadershipCard({ onCollapse }: LeadershipCardProps) {
  const { leadershipData, updateLeadershipData, leadershipCompletion, forceSave, saveStatus, isLocked, isReadOnly } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Leadership data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Calculate board diversity ratio
  const boardDiversityRatio = useMemo(() => {
    const female = parseFloat(leadershipData.femaleBoardMembers || "0") || 0;
    const male = parseFloat(leadershipData.maleBoardMembers || "0") || 0;
    const total = female + male;
    if (total === 0) return null;
    const femalePct = Math.round((female / total) * 100);
    const malePct = Math.round((male / total) * 100);
    return { female: femalePct, male: malePct };
  }, [leadershipData.femaleBoardMembers, leadershipData.maleBoardMembers]);

  const handleFemaleChange = (value: string) => {
    updateLeadershipData({ femaleBoardMembers: value });
    const female = parseFloat(value) || 0;
    const male = parseFloat(leadershipData.maleBoardMembers || "0") || 0;
    const total = female + male;
    if (total > 0) {
      const femalePct = Math.round((female / total) * 100);
      updateLeadershipData({ 
        boardMembersTotal: total.toString(),
        boardDiversityFemalePercent: femalePct.toString()
      });
    } else {
      updateLeadershipData({ 
        boardMembersTotal: "",
        boardDiversityFemalePercent: ""
      });
    }
  };

  const handleMaleChange = (value: string) => {
    updateLeadershipData({ maleBoardMembers: value });
    const female = parseFloat(leadershipData.femaleBoardMembers || "0") || 0;
    const male = parseFloat(value) || 0;
    const total = female + male;
    if (total > 0) {
      const femalePct = Math.round((female / total) * 100);
      updateLeadershipData({ 
        boardMembersTotal: total.toString(),
        boardDiversityFemalePercent: femalePct.toString()
      });
    } else {
      updateLeadershipData({ 
        boardMembersTotal: "",
        boardDiversityFemalePercent: ""
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Leadership (C9)"}
        description={"Track board composition and gender diversity in leadership"}
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Governance"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Governance Body / Board"}
                </h3>
                <HelpCircle className="h-[15px] w-[15px] shrink-0 text-slate-500" />
              </div>
            </div>
          }
        >
          <FieldBlock
            label={
              <>
                {"Governance Body / Board"} <span className="text-red-500">*</span>
              </>
            }
            description={"Does your company have a formal governance body or board of directors?"}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <ChoiceCard
                checked={leadershipData.hasGovernanceBody === true}
                disabled={disabled}
                label={"Yes"}
                name="governance_body"
                id="board_yes"
                onChange={() => updateLeadershipData({ hasGovernanceBody: true })}
              />
              <ChoiceCard
                checked={leadershipData.hasGovernanceBody === false}
                disabled={disabled}
                label={"No"}
                name="governance_body"
                id="board_no"
                onChange={() => updateLeadershipData({ hasGovernanceBody: false })}
              />
            </div>
          </FieldBlock>
        </SectionCard>

        {leadershipData.hasGovernanceBody === true && (
          <SectionCard
            header={
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"Board Gender Breakdown (If Yes)"}
                  </h3>
                  <HelpCircle className="h-[15px] w-[15px] shrink-0 text-slate-500" />
                </div>
              </div>
            }
          >
            <div className="flex flex-col gap-6">
              <p className="text-sm leading-5 text-slate-500">
                {"Enter the number of female and male board members to calculate gender diversity ratio"}
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <FieldBlock label={"Female board members"}>
                  <Input
                    type="number"
                    placeholder={"e.g. 2"}
                    min="0"
                    value={leadershipData.femaleBoardMembers || ""}
                    onChange={(e) => handleFemaleChange(e.target.value)}
                    disabled={disabled}
                    className="form-input h-12 rounded-xl px-4 text-base"
                  />
                </FieldBlock>

                <FieldBlock label={"Male board members"}>
                  <Input
                    type="number"
                    placeholder={"e.g. 3"}
                    min="0"
                    value={leadershipData.maleBoardMembers || ""}
                    onChange={(e) => handleMaleChange(e.target.value)}
                    disabled={disabled}
                    className="form-input h-12 rounded-xl px-4 text-base"
                  />
                </FieldBlock>
              </div>

              {boardDiversityRatio ? (
                <div className="rounded-xl border border-primary/10 bg-primary/5 p-4">
                  <p className="text-sm font-semibold leading-5 text-slate-900">
                    {"Gender Diversity Ratio"}
                  </p>
                  <p className="mt-2 text-sm leading-5 text-slate-600">
                    {`Female: ${boardDiversityRatio.female}%, Male: ${boardDiversityRatio.male}%`}
                    <span className="font-semibold text-primary">
                      {" "}
                      {"(calculated)"}
                    </span>
                  </p>
                </div>
              ) : null}

              <InfoCallout
                label={"Note:"}
                text={"Gender diversity in leadership is a key governance indicator for stakeholder trust and decision-making quality"}
              />
            </div>
          </SectionCard>
        )}

        <TransitionMeasuresBlock topicId="leadership" currentTab="governance" disabled={disabled} />

        <ProgressCard completion={leadershipCompletion} />
      </div>
    </div>
  );
}
